/**
 * Componente LoadingSpinner
 * Fecha: 16 Oct 2025
 */

import React from 'react';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

interface LoadingSpinnerProps {
  /** Tamaño del spinner */
  size?: SpinnerSize;
  /** Texto a mostrar debajo del spinner */
  text?: string;
  /** Color del spinner (clase de Tailwind) */
  color?: string;
  /** Si true, centra el spinner en toda la pantalla */
  fullScreen?: boolean;
  /** Clases adicionales */
  className?: string;
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-2',
  lg: 'w-12 h-12 border-3',
  xl: 'w-16 h-16 border-4',
};

/**
 * Spinner de carga animado
 * 
 * @example
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" text="Cargando empleados..." />
 * <LoadingSpinner fullScreen />
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  color = 'border-blue-600',
  fullScreen = false,
  className = '',
}) => {
  const spinner = (
    <div
      className={`
        ${sizeClasses[size]}
        border-gray-200
        ${color}
        border-t-transparent
        rounded-full
        animate-spin
        ${className}
      `}
      role="status"
      aria-label="Cargando"
    />
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-50">
        <div className="flex flex-col items-center gap-4">
          {spinner}
          {text && (
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (text) {
    return (
      <div className="flex flex-col items-center gap-3">
        {spinner}
        <p className="text-gray-600 dark:text-gray-400 text-sm">{text}</p>
      </div>
    );
  }

  return spinner;
};

/**
 * Skeleton loader para contenido que está cargando
 */
interface SkeletonProps {
  /** Ancho (clases de Tailwind o valor CSS) */
  width?: string;
  /** Alto (clases de Tailwind o valor CSS) */
  height?: string;
  /** Si true, usa forma circular */
  circle?: boolean;
  /** Número de líneas (para texto) */
  lines?: number;
  /** Clases adicionales */
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = 'w-full',
  height = 'h-4',
  circle = false,
  lines = 1,
  className = '',
}) => {
  if (lines > 1) {
    return (
      <div className={`space-y-3 ${className}`}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={`
              ${width}
              ${height}
              bg-gray-200 dark:bg-gray-700
              rounded
              animate-pulse
            `}
            style={{
              width: i === lines - 1 ? '80%' : undefined, // Última línea más corta
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`
        ${width}
        ${height}
        bg-gray-200 dark:bg-gray-700
        ${circle ? 'rounded-full' : 'rounded'}
        animate-pulse
        ${className}
      `}
    />
  );
};

/**
 * Loading state para tabla
 */
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 5,
  cols = 4,
}) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <Skeleton key={colIndex} width="flex-1" height="h-8" />
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * Loading state para card
 */
export const CardSkeleton: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
      <Skeleton width="w-32" height="h-6" />
      <Skeleton lines={3} />
      <div className="flex gap-2 pt-2">
        <Skeleton width="w-20" height="h-8" />
        <Skeleton width="w-20" height="h-8" />
      </div>
    </div>
  );
};
